/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

const FIXED_LABELS = {
  header: { key: "layerHeader", fallback: "Header" },
  heading: { key: "layerHeading", fallback: "Heading" },
  reissued: { key: "layerReissued", fallback: "Re-issuance Note" },
  table: { key: "layerTable", fallback: "Details Table" },
  footer: { key: "layerFooter", fallback: "Footer" }
};

function getDraftText(content) {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (typeof content === "object" && Array.isArray(content.blocks)) {
    return content.blocks.map((b) => b.text || "").join(" ");
  }
  return "";
}

function getElementLabel(el, t) {
  if (el.type === "fixed") {
    const entry = FIXED_LABELS[el.tag];
    return entry ? t(entry.key, entry.fallback) : t("layerFixed", "Fixed");
  }
  if (el.type === "custom-text") {
    const txt = getDraftText(el.content).trim().replace(/\s+/g, " ");
    if (!txt) return t("layerText", "Text");
    return txt.length > 24 ? txt.slice(0, 24) + "…" : txt;
  }
  if (el.type === "custom-image") {
    return t("layerImage", "Image");
  }
  return t("layer", "Layer");
}

function TypeIcon({ el }) {
  if (el.type === "custom-text") {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3 3h10v2h-1V4H9v8h1v1H6v-1h1V4H4v1H3V3z" fill="#495057" />
      </svg>
    );
  }
  if (el.type === "custom-image") {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="2" y="3" width="12" height="10" rx="1" stroke="#495057" strokeWidth="1.2" fill="none" />
        <circle cx="6" cy="7" r="1.2" fill="#495057" />
        <path d="M3 12l3-3 2 2 3-4 2 2" stroke="#495057" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // fixed
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="3" width="12" height="3" rx="0.5" fill="#495057" />
      <rect x="2" y="8" width="12" height="1" fill="#868686" />
      <rect x="2" y="10" width="12" height="1" fill="#868686" />
      <rect x="2" y="12" width="8" height="1" fill="#868686" />
    </svg>
  );
}

function EyeIcon({ hidden }) {
  if (hidden) {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2 8s2-4 6-4c1.3 0 2.5.4 3.5 1M14 8s-2 4-6 4c-1.3 0-2.5-.4-3.5-1" stroke="#868686" strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <path d="M2 2l12 12" stroke="#868686" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 8s2-4 6-4 6 4 6 4-2 4-6 4-6-4-6-4z" stroke="#495057" strokeWidth="1.2" fill="none" />
      <circle cx="8" cy="8" r="2" stroke="#495057" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1" stroke="#868686" strokeWidth="1.2" fill="none" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="#868686" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

function DragHandle() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
      <circle cx="3" cy="3" r="1" fill="#adb5bd" />
      <circle cx="7" cy="3" r="1" fill="#adb5bd" />
      <circle cx="3" cy="7" r="1" fill="#adb5bd" />
      <circle cx="7" cy="7" r="1" fill="#adb5bd" />
      <circle cx="3" cy="11" r="1" fill="#adb5bd" />
      <circle cx="7" cy="11" r="1" fill="#adb5bd" />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="#868686" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

TypeIcon.propTypes = {
  el: PropTypes.shape({
    type: PropTypes.string
  }).isRequired
};

EyeIcon.propTypes = {
  hidden: PropTypes.bool
};

function LayersPanel({
  elements,
  selectedElementId,
  onSelectElement,
  onReorder,
  onUpdateElement,
  onDelete,
  textObj = {}
}) {
  const t = (key, fallback) => textObj[key] || fallback;
  const [dragId, setDragId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null); // visual drop position in reversed list

  // Reverse so top-of-list = top layer (matches Figma/Photoshop convention).
  // Keep the underlying array untouched and translate on reorder.
  const reversed = elements.map((el, i) => ({ el, originalIndex: i })).reverse();

  const handleDragStart = (id) => (e) => {
    setDragId(id);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    } catch (_) {
      // Safari throws on some invocations; ignore
    }
  };

  const handleDragOver = (reversedIndex) => (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const isUpper = e.clientY < rect.top + rect.height / 2;
    setDropIndex(isUpper ? reversedIndex : reversedIndex + 1);
  };

  const handleDragLeaveList = (e) => {
    // Only clear if leaving the whole list, not just moving between rows
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropIndex(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!dragId || dropIndex == null) {
      setDragId(null);
      setDropIndex(null);
      return;
    }

    const sourceReversedIndex = reversed.findIndex((r) => r.el.id === dragId);
    if (sourceReversedIndex < 0) {
      setDragId(null);
      setDropIndex(null);
      return;
    }

    let targetReversedIndex = dropIndex;
    if (sourceReversedIndex < targetReversedIndex) targetReversedIndex -= 1;
    if (targetReversedIndex === sourceReversedIndex) {
      setDragId(null);
      setDropIndex(null);
      return;
    }

    const newReversed = reversed.slice();
    const [moved] = newReversed.splice(sourceReversedIndex, 1);
    newReversed.splice(targetReversedIndex, 0, moved);

    // Reverse back to original array order (end = top layer)
    const newElements = newReversed.map((r) => r.el).reverse();
    onReorder(newElements);

    setDragId(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDropIndex(null);
  };

  const handleToggleVisibility = useCallback(
    (el) => (e) => {
      e.stopPropagation();
      onUpdateElement(el.id, { visible: el.visible === false });
    },
    [onUpdateElement]
  );

  const handleDelete = useCallback(
    (el) => (e) => {
      e.stopPropagation();
      onDelete(el.id);
    },
    [onDelete]
  );

  if (!elements.length) {
    return (
      <div className="ReceiptEditor-layersPanel">
        <div className="ReceiptEditor-layersPanel-header">{t("layers", "Layers")}</div>
        <div className="ReceiptEditor-layersPanel-empty">
          {t("noLayers", "No elements on the receipt yet.")}
        </div>
      </div>
    );
  }

  return (
    <div className="ReceiptEditor-layersPanel">
      <div className="ReceiptEditor-layersPanel-header">{t("layers", "Layers")}</div>
      <div
        className="ReceiptEditor-layersPanel-list"
        onDragLeave={handleDragLeaveList}
        onDrop={handleDrop}
        onDragOver={(e) => {
          if (dragId) e.preventDefault();
        }}
      >
        {reversed.map(({ el }, i) => {
          const isSelected = el.id === selectedElementId;
          const isFixed = el.type === "fixed";
          const isHeader = isFixed && el.tag === "header";
          const isHidden = isFixed && el.visible === false;
          const isDragging = dragId === el.id;

          return (
            <React.Fragment key={el.id}>
              {dropIndex === i && (
                <div className="ReceiptEditor-layersPanel-dropIndicator" />
              )}
              <div
                className={
                  "ReceiptEditor-layersPanel-row" +
                  (isSelected ? " ReceiptEditor-layersPanel-row--selected" : "") +
                  (isDragging ? " ReceiptEditor-layersPanel-row--dragging" : "") +
                  (isHidden ? " ReceiptEditor-layersPanel-row--hidden" : "")
                }
                draggable
                onDragStart={handleDragStart(el.id)}
                onDragOver={handleDragOver(i)}
                onDragEnd={handleDragEnd}
                onClick={() => onSelectElement(el.id)}
              >
                <span className="ReceiptEditor-layersPanel-dragHandle">
                  <DragHandle />
                </span>
                <span className="ReceiptEditor-layersPanel-typeIcon">
                  <TypeIcon el={el} />
                </span>
                <span className="ReceiptEditor-layersPanel-label" title={getElementLabel(el, t)}>
                  {getElementLabel(el, t)}
                </span>
                {isHeader && (
                  <span
                    className="ReceiptEditor-layersPanel-lock"
                    title={t("layerLocked", "Position locked")}
                  >
                    <LockIcon />
                  </span>
                )}
                {isFixed && (
                  <button
                    type="button"
                    className="ReceiptEditor-layersPanel-iconBtn"
                    onClick={handleToggleVisibility(el)}
                    title={isHidden ? t("show", "Show") : t("hide", "Hide")}
                  >
                    <EyeIcon hidden={isHidden} />
                  </button>
                )}
                {!isFixed && (
                  <button
                    type="button"
                    className="ReceiptEditor-layersPanel-iconBtn"
                    onClick={handleDelete(el)}
                    title={t("delete", "Delete")}
                  >
                    <DeleteIcon />
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}
        {dropIndex === reversed.length && (
          <div className="ReceiptEditor-layersPanel-dropIndicator" />
        )}
      </div>
    </div>
  );
}

LayersPanel.propTypes = {
  elements: PropTypes.array.isRequired,
  selectedElementId: PropTypes.string,
  onSelectElement: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onUpdateElement: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  textObj: PropTypes.object
};

export default LayersPanel;
